import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";

import { useNavigationStore } from "@/components/nav/state";
import { DEFAULT_WORKSPACE_URL_SLUG } from "@/constants/core";
import { AppError } from "@/utils/errors";

import {
  extractFeatureFlags,
  FeatureFlags,
} from "../features/users/featureFlags";
import { getConnectionNodesTyped } from "../utils/relay";
import { GetBasicData } from "./queries.graphql";
import { useQueryWithResponseHandler } from "./useQueryWithResponseHandler";

type UseBasicDataProps = {
  fetch?: boolean;
};

type RouteParams = {
  workspace?: string;
};

export default function useBasicData(props?: UseBasicDataProps) {
  const params = useParams<RouteParams>();
  const { activeWorkspaceName, setActiveWorkspaceName } = useNavigationStore(); // TODO: Review this, odd coupling between a request and navigation
  const workspaceName = params.workspace;

  const hasWorkspaceName =
    !!workspaceName && workspaceName !== DEFAULT_WORKSPACE_URL_SLUG;

  const {
    state: query,
    execute: refreshBasicData,
    data: { me: user, workspace, activeWorkspace },
    error,
  } = useQueryWithResponseHandler(
    {
      query: GetBasicData,
      variables: {
        hasWorkspaceName,
        workspaceName: workspaceName ?? "",
        activeWorkspaceName: activeWorkspaceName ?? "",
        hasActiveWorkspaceName: !!activeWorkspaceName,
      },
      requestPolicy: props?.fetch
        ? "network-only"
        : import.meta.env.VITE_E2E_MOCK // TODO @tuizi yes this is bad... but until we review this useBasicData, cause of many issues, that's the simple fix
          ? "cache-and-network"
          : "cache-only", // TODO: @tuizi when SWR is enabled by default, remove this line and the fetch props
    },
    {},
  );

  if (error?.__typename === "Error" && error.code === "INTERNAL_SERVER_ERROR") {
    throw new AppError(error.message);
  }

  useEffect(() => {
    // Gracefully handle the case where the user is trying to access a workspace that they are not part of.
    // If we were not able to load the active workspace, simply reset it and the app will redirect to the home page.
    if (activeWorkspaceName && !activeWorkspace && error) {
      setActiveWorkspaceName(null);
    }
  }, [activeWorkspace, activeWorkspaceName, error, setActiveWorkspaceName]);

  const featureFlags = useMemo(
    () =>
      user
        ? extractFeatureFlags(user.featureFlags, workspace?.featureFlags ?? [])
        : ({} as FeatureFlags),
    [user, workspace],
  );

  const userWorkspaces = useMemo(() => {
    if (!user?.memberships) return [];

    return (
      getConnectionNodesTyped(user.memberships)
        .map((membership) => membership.workspace)
        // eslint-disable-next-line no-shadow
        .filter((workspace) => workspace != null)
    );
  }, [user?.memberships]);

  return {
    fetching: query.fetching,
    error,
    user,
    workspace: workspace || null,
    activeWorkspace,
    userWorkspaces,
    featureFlags,
    refreshBasicData,
  };
}
